import { Fn, useDebounceFn, useEventListener } from '@vueuse/core'
import type { Route, NavigationGuardNext, NavigationGuard } from 'vue-router'
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router/composables'
import PQueue from 'p-queue'
import { readonly, ref, watch } from 'vue'

import { useContext } from '@/composables/context'

const DEFAULT_DEBOUNCE_DELAY = 2000

export interface UseAutosaveOptions {
  confirmDiscardDescription?: string
  saveChangesCallback: () => Promise<void> | void
  debounceDelay?: number
  leaveGuard?: NavigationGuard
  updateGuard?: NavigationGuard
}

/**
 * Composable that provides autosave functionality.
 * It utilizes a queue to save changes sequentially and debounce functionality
 * to ensure that the save operation is triggered after a certain delay
 * of no further changes.
 * It also adds listeners to prevent the user from leaving the page without saving changes.
 */
export function useAutosave(options: UseAutosaveOptions) {
  const {
    confirmDiscardDescription,
    saveChangesCallback,
    debounceDelay,
    leaveGuard,
    updateGuard,
  } = options
  const { i18n } = useContext()

  const saveQueue = new PQueue({ concurrency: 1 })

  const isPending = ref(false)
  const isSaving = ref(false)
  const isActive = ref(false)

  const debounceSave = useDebounceFn(() => {
    if (isActive.value) {
      saveQueue.clear()

      saveQueue.add(async () => {
        isSaving.value = true
        await saveChangesCallback()
        isSaving.value = false
      })
    }
  }, debounceDelay ?? DEFAULT_DEBOUNCE_DELAY)

  const startAutosave = () => {
    isActive.value = true
    isPending.value = true
    debounceSave()
  }

  const stopAutosave = () => {
    isActive.value = false
    isSaving.value = false
    isPending.value = false
    saveQueue.clear()
  }

  saveQueue.on('idle', () => {
    isPending.value = false
  })

  // ---- Prevent leaving without saving ----

  // add beforeunload listener when changes are not saved to prevent the user from leaving the page without saving
  // removes the listener when the note is saved (otherwise it would gain performance issues on firefox)
  // see: https://developer.mozilla.org/en-US/docs/Web/API/Window/beforeunload_event
  let removeBeforeUnloadListener: Fn | undefined
  watch(isPending, (newIsPending) => {
    if (newIsPending) {
      removeBeforeUnloadListener = useEventListener('beforeunload', (event) => {
        if (isPending.value) {
          event.preventDefault()
        }
        return ''
      })
    } else if (removeBeforeUnloadListener) {
      removeBeforeUnloadListener()
      removeBeforeUnloadListener = undefined
    }
  })

  function confirmUnsavedChanges(
    _to: Route,
    _from: Route,
    next: NavigationGuardNext,
  ) {
    if (isPending.value) {
      if (
        confirm(
          confirmDiscardDescription ?? i18n.t('confirmDiscard').toString(),
        )
      ) {
        stopAutosave()
        next()
      } else {
        next(false)
      }
    } else {
      stopAutosave()
      next()
    }
  }

  // triggered when the user tries to navigate to a nother route
  onBeforeRouteLeave(leaveGuard ?? confirmUnsavedChanges)
  // triggered when the user tries to navigate to the same route with a new param
  onBeforeRouteUpdate(updateGuard ?? confirmUnsavedChanges)

  return {
    // isActive is true when the autosave is activated
    isActive: readonly(isActive),
    // isPending is true when there are unsaved changes in the queue or the
    // debounce delay is not yet reached
    isPending: readonly(isPending),
    // isSaving is true when the autosave is currently saving the changes
    isSaving: readonly(isSaving),
    startAutosave,
    stopAutosave,
  }
}
